import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Dialog } from '@/components/Dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { supabase } from '@/lib/supabase/client';
import { screens } from '@/lib/tailwind-screens';
import { RealtimeChat } from './realtime-chat';

function useIsSmOrLarger() {
    const query = `(min-width: ${screens.sm})`;
    const [isSm, setIsSm] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setIsSm(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [query]);
    return isSm;
}

type ChatDialogProps = {
    trigger: React.ReactNode;
    userId: string;
    userName: string;
    hideIfNoRooms?: boolean;
};

export function ChatDialog({ trigger, userId, userName, hideIfNoRooms = true }: ChatDialogProps) {
    const [rooms, setRooms] = useState<{ topic: string; room_name: string | null }[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const isSm = useIsSmOrLarger();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialChatParam = useRef(searchParams.get('chat'));

    useEffect(() => {
        const chatParam = initialChatParam.current;

        supabase
            .from('chat_rooms_users')
            .select('topic, chat_rooms(room_name)')
            .eq('user_id', userId)
            .then(({ data }) => {
                if (!data?.length) return;
                const roomList = data.map(r => ({ topic: r.topic, room_name: r.chat_rooms?.room_name ?? null }));
                setRooms(roomList);

                const topics = roomList.map(r => r.topic);
                if (chatParam && topics.includes(chatParam)) {
                    setSelectedRoom(chatParam);
                    setOpen(true);
                    setSearchParams({}, { replace: true });
                } else {
                    setSelectedRoom(topics[0]);
                }
            });
    }, [setSearchParams, userId]);

    if (hideIfNoRooms && !rooms?.length) return <></>;

    const selectedRoomName = rooms.find(r => r.topic === selectedRoom)?.room_name ?? selectedRoom;

    const title = (
        <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 w-fit mx-auto sm:mx-0 hover:opacity-70 transition-opacity">
                <span>{selectedRoomName ?? 'Välj rum'}</span>
                <ChevronDown className="size-4 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isSm ? 'start' : 'center'}>
                {rooms.map(room => (
                    <DropdownMenuItem
                        key={room.topic}
                        onSelect={() => setSelectedRoom(room.topic)}
                    >
                        {room.room_name ?? room.topic}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <Dialog trigger={trigger} title={title} open={open} onOpenChange={setOpen}>
            <div className="h-[80dvh]">
                {selectedRoom && (
                    <RealtimeChat
                        key={selectedRoom}
                        roomName={selectedRoom}
                        userId={userId}
                        username={userName}
                    />
                )}
            </div>
        </Dialog>
    );
}
